// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { ErrorFilled, WarningAltFilled } from '@carbon/icons-react';
import type { StatusToken } from '../../../../utils/entityHelpers';
import type { NodeFeed } from '../conductors/flowPulse';

export interface StatusMarkProps {
  status: StatusToken;
  /** Icon edge length in px. 12 is the drawing's default. */
  size?: number;
  /**
   * Whether power is reaching the asset. A fed dot brightens gently in step
   * with the flow dash arriving at it (`flowPulse.ts`); a quiet one is drawn
   * well below the pulse's dimmest point, so an asset with nothing reaching it
   * reads as off at a glance. Only the healthy/idle dot reacts: a warn or
   * fault mark never pulses and never dims, so a pulse can never be read as an
   * alarm and an alarm can never be hidden.
   */
  feed?: NodeFeed;
}

/**
 * How one asset's health reads, everywhere on the diagram.
 *
 * **The shape changes with the status, not only the colour.** `ok` and `idle`
 * are a dot, `warn` is a triangle, `fault` is a filled circle with a bang —
 * so the four states survive WCAG 1.4.1 and stay distinguishable in greyscale
 * print. Colour alone would fail exactly the two states an operator cannot
 * afford to miss.
 *
 * Extracted from `SldNode`, `SldBus` and `SldLoadGroup`, which each carried
 * their own copy of this ternary. Sharing it is what makes "status reads at the
 * same place, in the same form, on every element" enforceable rather than a
 * convention three files have to remember.
 *
 * `aria-hidden` on purpose: status is already in each element's accessible
 * name, and announcing it twice is noise.
 */
export const StatusMark = ({ status, size = 12, feed }: StatusMarkProps) => (
  <span
    className={`sld-status sld-status--${status}${
      feed === undefined ? '' : feed === 'quiet' ? ' sld-status--quiet' : ' sld-status--pulse'
    }`}
    style={
      feed && feed !== 'quiet'
        ? ({ '--sld-pulse-delay': `${Math.round(feed.delayMs)}ms` } as React.CSSProperties)
        : undefined
    }
    data-sld-feed={feed === undefined ? undefined : feed === 'quiet' ? 'quiet' : 'pulse'}
    aria-hidden="true"
  >
    {status === 'fault' ? (
      <ErrorFilled size={size} />
    ) : status === 'warn' ? (
      <WarningAltFilled size={size} />
    ) : (
      <span className="sld-status__dot" />
    )}
  </span>
);
